import ContentPage from '@/components/feature/ContentPage';

export default function ShippingPage() {
  return (
    <ContentPage
      title="CSSBuy Shipping Cost: Complete Guide to Rates"
      metaTitle="CSSBuy Shipping Cost 2026: Rates, Methods and Calculator Guide | CSSBuyBR"
      metaDescription="Complete CSSBuy shipping cost guide. Understand weight, volume, destination, methods and insurance. Never invent prices — always verify current rates."
      subtitle="Shipping costs can vary significantly. Understand the factors that affect pricing before making your purchase."
      showToc
      sections={[
        { id: 'factors', title: 'Cost Factors' },
        { id: 'methods', title: 'Shipping Methods' },
        { id: 'weight', title: 'Weight & Volume' },
        { id: 'insurance', title: 'Insurance' },
        { id: 'calculation', title: 'How Costs Are Calculated' },
        { id: 'tips', title: 'Money-Saving Tips' },
        { id: 'faq', title: 'FAQ' },
      ]}
      content={
        <div className="space-y-6">
          <section id="factors">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">What Affects CSSBuy Shipping Cost?</h2>
            <p>Several variables determine the final shipping cost for your CSSBuy orders. Understanding these factors helps you plan better and avoid surprises.</p>
            <div className="grid sm:grid-cols-2 gap-3 my-4">
              {[
                'Actual weight of the package',
                'Volumetric weight (dimensions)',
                'Destination country and region',
                'Chosen shipping carrier',
                'Selected shipping speed',
                'Insurance coverage level',
                'Package consolidation status',
                'Customs and duties (varies by country)',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-background-100 text-sm text-foreground-700">
                  <i className="ri-truck-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="methods">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Shipping Methods Explained</h2>
            <p>CSSBuy typically offers multiple shipping options. The best choice depends on your budget, timeline, and item characteristics.</p>
            <div className="overflow-x-auto my-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Method</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Typical Timeline</th>
                    <th className="text-left px-3 py-2 font-medium text-foreground-700">Best For</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/70">
                  {[
                    ['Express Courier', '3-10 days', 'Small, valuable or urgent items'],
                    ['Air Mail', '10-25 days', 'Light to medium weight packages'],
                    ['E-Packet', '15-35 days', 'Small items under weight limits'],
                    ['Sea Freight', '30-60 days', 'Very heavy or bulky orders'],
                    ['Rail Freight', '20-40 days', 'Heavy items to Europe'],
                  ].map(([m, t, b], i) => (
                    <tr key={i}>
                      <td className="px-3 py-2 font-medium text-foreground-800">{m}</td>
                      <td className="px-3 py-2 text-foreground-600">{t}</td>
                      <td className="px-3 py-2 text-foreground-600">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-foreground-500 italic">Note: Timelines are estimates and can vary based on customs processing, weather, and carrier conditions. Always check current estimates on the official website.</p>
          </section>

          <section id="weight">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Weight vs. Volumetric Weight</h2>
            <p>Carriers charge based on whichever is greater: actual weight or volumetric weight. Volumetric weight is calculated using the package dimensions.</p>
            <p className="mt-2">If your package is light but bulky (like a large box of shoes), you may be charged for the space it occupies rather than its actual weight. Consolidating items into compact packaging can reduce volumetric charges.</p>
          </section>

          <section id="insurance">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Insurance Options</h2>
            <p>Insurance protects your shipment against loss, damage, or theft during transit. Consider insurance for:</p>
            <ul className="list-disc list-inside space-y-1.5 ml-2">
              <li>High-value items</li>
              <li>Fragile products</li>
              <li>Shipments to regions with higher delivery risks</li>
              <li>First-time purchases from unfamiliar sellers</li>
            </ul>
            <p className="mt-2">Insurance terms and coverage limits vary by carrier. Read the details carefully before selecting.</p>
          </section>

          <section id="calculation">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">How Costs Are Calculated</h2>
            <p>CSSBuy shipping costs are typically calculated as follows:</p>
            <ol className="list-decimal list-inside space-y-1.5 ml-2">
              <li>First weight bracket (base fee for initial weight)</li>
              <li>Additional weight brackets (per-unit fees for extra weight)</li>
              <li>Carrier handling fees</li>
              <li>Fuel surcharges (if applicable)</li>
              <li>Insurance (if selected)</li>
              <li>Customs processing fees (where applicable)</li>
            </ol>
            <p className="mt-2">The exact formula varies by carrier and destination. Always use the official shipping calculator on the CSSBuy website for current quotes.</p>
          </section>

          <section id="tips">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Money-Saving Tips</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Consolidate multiple items into one package',
                'Remove unnecessary packaging before shipping',
                'Choose slower shipping for non-urgent orders',
                'Compare carrier rates for your destination',
                'Avoid peak shipping seasons when possible',
                'Group lightweight items together efficiently',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-accent-50 text-sm text-foreground-700">
                  <i className="ri-lightbulb-line text-accent-500 mt-0.5"></i>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          <section id="faq">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-3">Shipping FAQ</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">Can I change shipping method after ordering?</h3>
                <p className="text-sm">It depends on the order status. Before warehouse processing, changes may be possible. After shipping, it is usually not possible.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">What happens if my package is lost?</h3>
                <p className="text-sm">If you purchased insurance, file a claim with the carrier. Without insurance, recovery options are limited. Always consider insurance for valuable shipments.</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground-800 mb-1">Do I need to pay customs duties?</h3>
                <p className="text-sm">Customs duties depend on your country, the item value, and the item category. Research your local import regulations before ordering.</p>
              </div>
            </div>
          </section>

          <div className="mt-10 p-4 rounded-xl bg-primary-50 border border-primary-200/70">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">Check Current Rates</h3>
            <p className="text-sm text-primary-700 mb-3">Shipping rates change frequently. Visit the main site to see the most current pricing for your destination.</p>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors">
              Visit Main Site
              <i className="ri-external-link-line"></i>
            </a>
          </div>
        </div>
      }
    />
  );
}